import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import { AdminOwnerDetail, AdminOwnerListItem } from '../types';

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const OwnersPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [owners, setOwners] = useState<AdminOwnerListItem[]>([]);
  const [detail, setDetail] = useState<AdminOwnerDetail | null>(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');

  const loadData = useCallback(async (nextSearch = '') => {
    setLoading(true);
    setError('');

    try {
      const payload = await adminService.listOwners(nextSearch);
      setOwners(payload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar owners');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (ownerUserId: string) => {
    setSelectedOwnerId(ownerUserId);
    setDetailLoading(true);
    setDetailError('');

    try {
      const payload = await adminService.getOwnerDetail(ownerUserId);
      setDetail(payload);
    } catch (nextError: any) {
      setDetail(null);
      setDetailError(nextError.message || 'Erro ao carregar detalhe do owner');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData('');
  }, [loadData]);

  const metricCards = useMemo(
    () =>
      detail
        ? [
            {
              eyebrow: 'Clubs',
              title: 'Ativos',
              value: String(detail.summary.active_clubs_count),
              tone: 'emerald' as const,
            },
            {
              eyebrow: 'Players',
              title: 'Base ativa',
              value: String(detail.summary.active_members),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Mesas',
              title: 'Ativas',
              value: String(detail.summary.active_tables),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Rake',
              title: 'Ultimos 30 dias',
              value: formatAmount(detail.summary.rake_30d),
              tone: 'amber' as const,
            },
            {
              eyebrow: 'Gateway',
              title: 'Custo 30d',
              value: formatAmount(detail.summary.gateway_fee_30d),
              tone: 'rose' as const,
            },
            {
              eyebrow: 'Atencao',
              title: 'Clubs com setup fragil',
              value: String(detail.summary.clubs_with_payment_issues),
              tone: 'slate' as const,
            },
          ]
        : [],
    [detail]
  );

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Owners</p>
          <h1>Operadores e assinatura</h1>
          <p>Lista global com detalhe operacional e comercial por owner, sem abrir mutacao.</p>
        </div>
        <div className="admin-topbar__actions">
          {detail ? <span className="admin-chip">Selecionado: {detail.profile.owner_name}</span> : null}
          <button type="button" className="admin-secondary-button" onClick={() => void loadData(search)} disabled={loading}>
            {loading ? 'Buscando...' : 'Atualizar lista'}
          </button>
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-toolbar">
          <input
            className="admin-search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por owner, email ou CPF"
          />
          <button type="button" className="admin-secondary-button" onClick={() => void loadData(search)} disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Owner</th>
                <th>Plano</th>
                <th>Status</th>
                <th>Clubs</th>
                <th>Ativos</th>
                <th>Rake</th>
                <th>Expira</th>
                <th>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {owners.length ? (
                owners.map((owner) => (
                  <tr key={owner.owner_user_id} className={selectedOwnerId === owner.owner_user_id ? 'admin-table__row--selected' : ''}>
                    <td>
                      <strong>{owner.owner_name}</strong>
                      <span>{owner.owner_email}</span>
                    </td>
                    <td>{owner.plan_code || '-'}</td>
                    <td>{owner.subscription_status || '-'}</td>
                    <td>{owner.clubs_count}</td>
                    <td>{owner.active_clubs_count}</td>
                    <td>{formatAmount(owner.total_rake)}</td>
                    <td>{formatDateTime(owner.expires_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-link-chip admin-link-chip--button"
                        onClick={() => void loadDetail(owner.owner_user_id)}
                        disabled={detailLoading && selectedOwnerId === owner.owner_user_id}
                      >
                        {selectedOwnerId === owner.owner_user_id ? (detailLoading ? 'Carregando...' : 'Recarregar') : 'Detalhar'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>Nenhum owner encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {detailError ? <div className="admin-banner admin-banner--error">{detailError}</div> : null}

      {detail ? (
        <>
          <section className="admin-metrics-grid">
            {metricCards.map((card) => (
              <MetricCard key={card.title} {...card} />
            ))}
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Perfil</p>
                  <h2>Identidade do owner</h2>
                </div>
                <span className="admin-chip">Atualizado em {formatDateTime(detail.generated_at)}</span>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair">
                  <span>Nome</span>
                  <strong>{detail.profile.owner_name}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Email</span>
                  <strong>{detail.profile.owner_email || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>CPF</span>
                  <strong>{detail.profile.cpf || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Telefone</span>
                  <strong>{detail.profile.phone || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Papel</span>
                  <strong>{detail.profile.role}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Ultimo login</span>
                  <strong>{formatDateTime(detail.profile.last_login)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Status de conta</span>
                  <strong>{detail.profile.is_banned ? 'BLOQUEADO' : 'ATIVO'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Criado em</span>
                  <strong>{formatDateTime(detail.profile.created_at)}</strong>
                </div>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Assinatura</p>
                  <h2>Leitura comercial atual</h2>
                </div>
                <span className="admin-chip">{detail.subscription.subscription_status || 'SEM STATUS'}</span>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair">
                  <span>Plano</span>
                  <strong>{detail.subscription.plan_code || '-'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Inicio</span>
                  <strong>{formatDateTime(detail.subscription.started_at)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Expira</span>
                  <strong>{formatDateTime(detail.subscription.expires_at)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Ultimo pagamento</span>
                  <strong>{formatDateTime(detail.subscription.last_payment_at)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Cancelado em</span>
                  <strong>{formatDateTime(detail.subscription.cancelled_at)}</strong>
                </div>
                <div className="admin-detail-pair admin-detail-pair--full">
                  <span>Notas</span>
                  <strong>{detail.subscription.notes || 'Sem observacao comercial registrada.'}</strong>
                </div>
              </div>

              <div className="admin-kpi-grid">
                <article className="admin-kpi-item">
                  <span>Clubs totais</span>
                  <strong>{detail.summary.clubs_count}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Players ativos</span>
                  <strong>{detail.summary.active_members}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Depositos 30d</span>
                  <strong>{formatAmount(detail.summary.deposits_30d)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Saques 30d</span>
                  <strong>{formatAmount(detail.summary.withdrawals_30d)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Rake total</span>
                  <strong>{formatAmount(detail.summary.total_rake)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Ultima atividade</span>
                  <strong>{formatDateTime(detail.summary.last_activity_at)}</strong>
                </article>
              </div>
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Clubs</p>
                  <h2>Carteira operacional</h2>
                </div>
                <span className="admin-chip">{detail.clubs.length} club(s)</span>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Club</th>
                      <th>Status</th>
                      <th>Membros</th>
                      <th>Mesas</th>
                      <th>Rake 30d</th>
                      <th>Gateway 30d</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.clubs.length ? (
                      detail.clubs.map((club) => (
                        <tr key={club.club_id}>
                          <td>
                            <strong>{club.club_name}</strong>
                            <span>{club.club_code}</span>
                          </td>
                          <td>
                            <strong>{club.is_active ? 'ATIVO' : 'INATIVO'}</strong>
                            <span>{club.payment_status || 'SEM CONTA'}</span>
                          </td>
                          <td>{club.total_members}</td>
                          <td>{club.active_tables}</td>
                          <td>
                            <strong>{formatAmount(club.rake_30d)}</strong>
                            <span>Depositos: {formatAmount(club.deposits_30d)}</span>
                          </td>
                          <td>
                            <strong>{formatAmount(club.gateway_fee_30d)}</strong>
                            <span>{formatDateTime(club.last_activity_at)}</span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>Nenhum club vinculado a este owner.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Historico</p>
                  <h2>Ultimos eventos de assinatura</h2>
                </div>
                <span className="admin-chip">{detail.subscription_history.length} evento(s)</span>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Status</th>
                      <th>Plano</th>
                      <th>Evento</th>
                      <th>Responsavel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.subscription_history.length ? (
                      detail.subscription_history.map((entry) => (
                        <tr key={entry.id}>
                          <td>{formatDateTime(entry.recorded_at)}</td>
                          <td>
                            <strong>{entry.status}</strong>
                            <span>{formatDateTime(entry.expires_at)}</span>
                          </td>
                          <td>{entry.plan_code}</td>
                          <td>
                            <strong>{entry.change_type}</strong>
                            <span>{entry.notes || '-'}</span>
                          </td>
                          <td>{entry.created_by_name || 'Sistema'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>Nenhum evento de assinatura encontrado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          </section>

          <section className="admin-section-card">
            <div className="admin-section-card__header">
              <div>
                <p className="admin-section-eyebrow">Escopo</p>
                <h2>Limites desta etapa</h2>
              </div>
            </div>

            <div className="admin-note-stack">
              {detail.notes.map((note) => (
                <div key={note} className="admin-note-chip">
                  {note}
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <section className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Detalhe</p>
              <h2>Selecione um owner para abrir o contexto completo</h2>
            </div>
            <span className="admin-chip">{detailLoading ? 'Carregando detalhe' : 'Sem selecao'}</span>
          </div>

          <div className="admin-note-stack">
            <div className="admin-note-chip">A tela preserva a listagem atual e adiciona drill-down somente leitura.</div>
            <div className="admin-note-chip">O historico comercial vem da trilha de subscription ja existente no backend.</div>
            <div className="admin-note-chip">Mutacoes em plano, bloqueio e configuracao continuam fora desta etapa.</div>
          </div>
        </section>
      )}
    </div>
  );
};

export default OwnersPage;
